import { useEffect, useState } from "react";
import Password from "../Components/Fields/Password";
import String from "../Components/Fields/String";
import {
  ErrorAlert,
  login,
  PasswordNotification,
  SuccessAlert,
  WarningAlert,
} from "../functions/api";
import { useStore } from "../Components/StateProvider";

const Login = () => {
  const store = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const init = {
    username: "",
    password: "",
    devicetoken: "",
  };

  useEffect(() => {
    store.setmodule("Loginpage", init);
    store.setheader("Hello, Admin");
  }, []);

  function HandleLogin() {
    setIsSubmitting(true);
    login(store);
    // Reset submitting state after a delay as a fallback (the api function redirects or shows error alert)
    setTimeout(() => {
      setIsSubmitting(false);
    }, 2500);
  }

  const handleKeyPress = (event) => {
    if (event.keyCode === 13 || event.which === 13) {
      HandleLogin();
    }
  };

  const HandleForgotPassword = () => {
    let data = store.getrecord();
    const usernameOrEmail = data.username;
    if (!usernameOrEmail) {
      WarningAlert("Enter UserName Or Email");
      return;
    }
    PasswordNotification(usernameOrEmail).then((res) => {
      if (res.success) {
        SuccessAlert("Change Password Requested");
      } else {
        ErrorAlert(res.message || "Failed to request password change.");
      }
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#f8fafc] relative overflow-hidden font-sans">
      {/* Google Fonts import for Outfit and Inter */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@500;600;700;800&display=swap');
        body {
          font-family: 'Inter', sans-serif;
          background-color: #f8fafc;
          color: #1e293b;
        }
      `}</style>

      {/* Grid Pattern Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.6]"></div>

      {/* Modern Mesh Gradient Blurs */}
      <div className="absolute top-[10%] left-[10%] w-[350px] h-[350px] bg-[#4f46e5]/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[10%] w-[400px] h-[400px] bg-[#805ad5]/15 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute top-[40%] right-[25%] w-[250px] h-[250px] bg-[#3b82f6]/10 rounded-full blur-[90px] pointer-events-none"></div>

      {/* Main Container Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4">
        {/* Logo and Branding Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-28 h-20 rounded-[20px] bg-[#000042] shadow-[0_10px_30px_rgba(0,0,66,0.15)] border border-white/10 mb-4 transform hover:scale-105 transition-all duration-300">
            <img
              src="/Assets/images/Logo.png"
              alt="Absolute Legal Logo"
              className="w-22 h-14 object-contain"
            />
          </div>
          <h1 className="text-3xl font-extrabold text-[#000042] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Absolute Legal
          </h1>
          <p className="text-sm text-[#64748b] mt-2 text-center font-medium">
            Management Suite for Legal, Corporate & Operations
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#ffffff] rounded-[24px] border border-[#e2e8f0] shadow-[0_20px_50px_rgba(0,0,0,0.06)] p-8 md:p-10">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#1e293b] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign In
            </h2>
            <p className="text-xs text-[#94a3b8] mt-1 font-medium">
              Enter your credentials to access your workplace portal
            </p>
          </div>

          <div className="flex flex-col gap-5">
            <String
              store={store}
              name="username"
              label="Username or Email Address"
              className={{
                field: "flex flex-col gap-1.5",
                input: "w-full px-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#4f46e5] focus:bg-[#ffffff] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)] transition-all duration-300",
                lable: "w-fit text-xs font-semibold text-[#475569]"
              }}
              doubleline={true}
              onKeyPress={handleKeyPress}
            />

            <Password
              store={store}
              name="password"
              label="Password"
              className="w-full px-4 py-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-sm text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:border-[#4f46e5] focus:bg-[#ffffff] focus:shadow-[0_0_0_4px_rgba(79,70,229,0.1)] transition-all duration-300"
              labelClassName="w-fit text-xs font-semibold text-[#475569]"
              onkeypress={handleKeyPress}
            />
            
            <div className="flex items-center justify-between text-xs mt-1">
              <div></div>
              <button
                className="text-[#4f46e5] hover:text-[#3730a3] font-semibold bg-transparent border-0 cursor-pointer p-0 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  HandleForgotPassword();
                }}
              >
                Forgot password?
              </button>
            </div>
          </div>

          <div className="mt-8">
            <button
              disabled={isSubmitting}
              className={`w-full py-3 bg-[#000042] text-[#ffffff] rounded-xl font-semibold shadow-md hover:bg-[#09095c] hover:shadow-lg active:scale-[0.98] transition-all border-0 cursor-pointer text-sm flex items-center justify-center gap-2 ${
                isSubmitting ? "opacity-80 cursor-not-allowed" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                HandleLogin();
              }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing In...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#94a3b8] font-medium">
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
